import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore';
import { db, auth } from './firebase';
import './HolidayManagement.css';

function HolidayManagement({ onClose }) {
  const [holidays, setHolidays] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [showPublicHolidaysModal, setShowPublicHolidaysModal] = useState(false);

  const [formData, setFormData] = useState({
    holidayName: '',
    holidayType: 'public',
    startDate: '',
    endDate: '',
    isSingleDay: true,
    appliesToAllSchools: true,
    schoolRef: '',
    notes: ''
  });

  // South African Public Holidays 2026
  const publicHolidays2026 = [
    { name: "New Year's Day", date: "2026-01-01" },
    { name: "Human Rights Day", date: "2026-03-21" },
    { name: "Good Friday", date: "2026-04-03" },
    { name: "Family Day", date: "2026-04-06" },
    { name: "Freedom Day", date: "2026-04-27" },
    { name: "Workers' Day", date: "2026-05-01" },
    { name: "Youth Day", date: "2026-06-16" },
    { name: "National Women's Day", date: "2026-08-09" },
    { name: "Heritage Day", date: "2026-09-24" },
    { name: "Day of Reconciliation", date: "2026-12-16" },
    { name: "Christmas Day", date: "2026-12-25" },
    { name: "Day of Goodwill", date: "2026-12-26" }
  ];

  const [selectedPublicHolidays, setSelectedPublicHolidays] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Load holidays
      const holidaysQuery = query(
        collection(db, 'holidays'),
        where('franchiseeRef', '==', franchiseeRef),
        orderBy('startDate', 'asc')
      );
      const holidaysSnapshot = await getDocs(holidaysQuery);
      const holidaysList = holidaysSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setHolidays(holidaysList);

      // Load schools for dropdown
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schoolsList = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);

    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isSingleDay') {
      setFormData(prev => ({
        ...prev,
        isSingleDay: checked,
        endDate: checked ? prev.startDate : prev.endDate
      }));
    } else if (name === 'startDate' && formData.isSingleDay) {
      setFormData(prev => ({
        ...prev,
        startDate: value,
        endDate: value
      }));
    } else if (name === 'appliesToAllSchools') {
      setFormData(prev => ({
        ...prev,
        appliesToAllSchools: checked,
        schoolRef: checked ? '' : prev.schoolRef
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Validate dates
      if (!formData.startDate) {
        alert('Start date is required');
        setLoading(false);
        return;
      }

      if (!formData.isSingleDay && !formData.endDate) {
        alert('End date is required for date range holidays');
        setLoading(false);
        return;
      }

      if (!formData.isSingleDay && new Date(formData.endDate) < new Date(formData.startDate)) {
        alert('End date must be after start date');
        setLoading(false);
        return;
      }

      const holidayData = {
        franchiseeRef: franchiseeRef,
        schoolRef: formData.appliesToAllSchools ? null : doc(db, 'schools', formData.schoolRef),
        holidayName: formData.holidayName,
        holidayType: formData.holidayType,
        startDate: new Date(formData.startDate + 'T00:00:00'),
        endDate: new Date((formData.isSingleDay ? formData.startDate : formData.endDate) + 'T00:00:00'),
        isSingleDay: formData.isSingleDay,
        appliesToAllSchools: formData.appliesToAllSchools,
        autoDetected: false,
        linkedTermStructureRef: null,
        notes: formData.notes,
        createdDate: new Date(),
        createdBy: user.email
      };

      if (editingHoliday) {
        await updateDoc(doc(db, 'holidays', editingHoliday.id), {
          ...holidayData,
          lastModified: new Date()
        });
        alert('Holiday updated successfully!');
      } else {
        await addDoc(collection(db, 'holidays'), holidayData);
        alert('Holiday added successfully!');
      }

      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving holiday:', error);
      alert('Error saving holiday. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      holidayName: holiday.holidayName,
      holidayType: holiday.holidayType,
      startDate: holiday.startDate.toDate().toISOString().split('T')[0],
      endDate: holiday.endDate.toDate().toISOString().split('T')[0],
      isSingleDay: holiday.isSingleDay,
      appliesToAllSchools: holiday.appliesToAllSchools,
      schoolRef: holiday.schoolRef?.id || '',
      notes: holiday.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'holidays', holidayId));
      alert('Holiday deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      alert('Error deleting holiday');
    }
  };

  const resetForm = () => {
    setFormData({
      holidayName: '',
      holidayType: 'public',
      startDate: '',
      endDate: '',
      isSingleDay: true,
      appliesToAllSchools: true,
      schoolRef: '',
      notes: ''
    });
    setEditingHoliday(null);
    setShowAddModal(false);
  };

  const handleQuickAddPublicHolidays = async () => {
    if (selectedPublicHolidays.length === 0) {
      alert('Please select at least one holiday');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      for (const holidayDate of selectedPublicHolidays) {
        const holiday = publicHolidays2026.find(h => h.date === holidayDate);
        
        await addDoc(collection(db, 'holidays'), {
          franchiseeRef: franchiseeRef,
          schoolRef: null,
          holidayName: holiday.name,
          holidayType: 'public',
          startDate: new Date(holiday.date + 'T00:00:00'),
          endDate: new Date(holiday.date + 'T00:00:00'),
          isSingleDay: true,
          appliesToAllSchools: true,
          autoDetected: false,
          linkedTermStructureRef: null,
          notes: 'Public holiday - no lessons',
          createdDate: new Date(),
          createdBy: user.email
        });
      }

      alert(`${selectedPublicHolidays.length} public holidays added successfully!`);
      setSelectedPublicHolidays([]);
      setShowPublicHolidaysModal(false);
      loadData();
    } catch (error) {
      console.error('Error adding public holidays:', error);
      alert('Error adding public holidays');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const togglePublicHoliday = (date) => {
    setSelectedPublicHolidays(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  const selectAllPublicHolidays = () => {
    if (selectedPublicHolidays.length === publicHolidays2026.length) {
      setSelectedPublicHolidays([]);
    } else {
      setSelectedPublicHolidays(publicHolidays2026.map(h => h.date));
    }
  };

  if (loading && holidays.length === 0) {
    return (
      <div className="holiday-management-container">
        <div className="loading">Loading holidays...</div>
      </div>
    );
  }

  return (
    <div className="holiday-management-container">
      <div className="holiday-header">
        <div className="header-left">
          <h1>🗓️ Holiday Management</h1>
          <p>Manage holidays, term breaks, and school closures</p>
        </div>
      </div>

      <div className="holiday-actions">
        <button onClick={() => setShowAddModal(true)} className="btn-add">
          + Add Holiday
        </button>
        <button onClick={() => setShowPublicHolidaysModal(true)} className="btn-quick-add">
          ⚡ Quick Add Public Holidays
        </button>
      </div>

      {holidays.length === 0 ? (
        <div className="no-holidays">
          <p>📅 No holidays added yet</p>
          <p>Add holidays to prevent lessons from being scheduled on those dates</p>
        </div>
      ) : (
        <div className="holidays-list">
          {holidays.map(holiday => (
            <div key={holiday.id} className="holiday-card">
              <div className="holiday-card-header">
                <div className="holiday-title">
                  <h3>{holiday.holidayName}</h3>
                  <span className={`holiday-type-badge ${holiday.holidayType}`}>
                    {holiday.holidayType}
                  </span>
                  {holiday.autoDetected && (
                    <span className="auto-badge">AUTO</span>
                  )}
                </div>
                <div className="holiday-actions-btns">
                  <button onClick={() => handleEdit(holiday)} className="btn-edit-small">
                    ✏️ Edit
                  </button>
                  <button onClick={() => handleDelete(holiday.id)} className="btn-delete-small">
                    🗑️ Delete
                  </button>
                </div>
              </div>

              <div className="holiday-details">
                <div className="holiday-date">
                  📅 {formatDate(holiday.startDate)}
                  {!holiday.isSingleDay && ` - ${formatDate(holiday.endDate)}`}
                  <span className="day-count">
                    ({holiday.isSingleDay ? '1 day' : `${Math.ceil((holiday.endDate.toDate() - holiday.startDate.toDate()) / (1000 * 60 * 60 * 24)) + 1} days`})
                  </span>
                </div>

                <div className="holiday-applies">
                  🏫 {holiday.appliesToAllSchools ? 'All Schools' : 'Specific School'}
                </div>

                {holiday.notes && (
                  <div className="holiday-notes">
                    📝 {holiday.notes}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Holiday Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => !loading && resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
              <button onClick={resetForm} className="modal-close" disabled={loading}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="holiday-form">
              <div className="form-group">
                <label>Holiday Name *</label>
                <input
                  type="text"
                  name="holidayName"
                  value={formData.holidayName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Youth Day, Term 1 Break"
                />
              </div>

              <div className="form-group">
                <label>Holiday Type *</label>
                <select
                  name="holidayType"
                  value={formData.holidayType}
                  onChange={handleChange}
                  required
                >
                  <option value="public">Public Holiday</option>
                  <option value="school">School Holiday</option>
                  <option value="custom">Custom/Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isSingleDay"
                    checked={formData.isSingleDay}
                    onChange={handleChange}
                  />
                  <span>Single day holiday</span>
                </label>
                <small>Uncheck for date range (e.g., term breaks)</small>
              </div>

              {!formData.isSingleDay && (
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required={!formData.isSingleDay}
                    min={formData.startDate}
                  />
                </div>
              )}

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="appliesToAllSchools"
                    checked={formData.appliesToAllSchools}
                    onChange={handleChange}
                  />
                  <span>Applies to all schools</span>
                </label>
              </div>

              {!formData.appliesToAllSchools && (
                <div className="form-group">
                  <label>Select School *</label>
                  <select
                    name="schoolRef"
                    value={formData.schoolRef}
                    onChange={handleChange}
                    required={!formData.appliesToAllSchools}
                  >
                    <option value="">-- Select School --</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>
                        {school.schoolName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Optional notes..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={resetForm} className="btn-cancel" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading ? 'Saving...' : (editingHoliday ? 'Update Holiday' : 'Add Holiday')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Public Holidays Modal */}
      {showPublicHolidaysModal && (
        <div className="modal-overlay" onClick={() => setShowPublicHolidaysModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚡ Quick Add: 2026 Public Holidays</h2>
              <button onClick={() => setShowPublicHolidaysModal(false)} className="modal-close">✕</button>
            </div>

            <div className="public-holidays-list">
              <div className="select-all-row">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedPublicHolidays.length === publicHolidays2026.length}
                    onChange={selectAllPublicHolidays}
                  />
                  <span><strong>Select All</strong></span>
                </label>
              </div>

              {publicHolidays2026.map(holiday => (
                <div key={holiday.date} className="public-holiday-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedPublicHolidays.includes(holiday.date)}
                      onChange={() => togglePublicHoliday(holiday.date)}
                    />
                    <span>
                      <strong>{holiday.name}</strong>
                      <small>{new Date(holiday.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</small>
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowPublicHolidaysModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={handleQuickAddPublicHolidays} 
                className="btn-save"
                disabled={selectedPublicHolidays.length === 0 || loading}
              >
                {loading ? 'Adding...' : `Add ${selectedPublicHolidays.length} Holidays`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HolidayManagement;